export type CountAlias = { key: string; alias: string }

export type CountQuery = { query: string; variables: Record<string, string>; aliases: CountAlias[] }

/**
 * One batched count per entry, against `contacts` unless the entry names its entity,
 * aliased so a single request answers every list. Keys become aliases by replacing
 * anything GraphQL rejects in a name, and a key that starts with a digit (a list id) gets a
 * leading underscore, since one invalid alias fails the whole query.
 */
export function buildCountQuery(
  name: string,
  entries: { key: string; search: string; entity?: string }[],
): CountQuery | undefined {
  if (entries.length === 0) return undefined
  const aliases = entries.map(({ key }) => ({
    key,
    alias: `${/^[0-9]/.test(key) ? `_` : ``}${key.replace(/[^A-Za-z0-9_]/g, `_`)}_total`,
  }))
  const variableDefs = aliases.map(({ alias }) => `$${alias}Search: String`).join(`, `)
  const fields = aliases
    .map(
      ({ alias }, index) =>
        `${alias}: ${entries[index].entity ?? `contacts`}(search: $${alias}Search, pagination: { limit: 0 }) { total }`,
    )
    .join(`\n`)
  const variables: Record<string, string> = {}
  entries.forEach(({ search }, index) => {
    variables[`${aliases[index].alias}Search`] = search
  })
  return { query: `query ${name}(${variableDefs}) { ${fields} }`, variables, aliases }
}

export function readCounts(
  data: Record<string, { total: number } | undefined> | undefined,
  aliases: CountAlias[],
): Record<string, number> {
  if (!data) return {}
  const counts: Record<string, number> = {}
  for (const { key, alias } of aliases) counts[key] = data[alias]?.total ?? 0
  return counts
}

/**
 * One batched member total per list, aliased like `buildCountQuery` so `readCounts` reads it.
 * Returns `undefined` for an empty list.
 */
export function buildListCountQuery(name: string, listIds: readonly string[]): CountQuery | undefined {
  if (listIds.length === 0) return undefined
  const aliases = listIds.map((key) => ({ key, alias: `l_${key.replace(/[^A-Za-z0-9_]/g, `_`)}_total` }))
  const variableDefs = aliases.map(({ alias }) => `$${alias}Id: ID!`).join(`, `)
  const fields = aliases
    .map(({ alias }) => `${alias}: listMembers(listId: $${alias}Id, pagination: { limit: 0 }) { total }`)
    .join(`\n`)
  const variables: Record<string, string> = {}
  aliases.forEach(({ key, alias }) => {
    variables[`${alias}Id`] = key
  })
  return { query: `query ${name}(${variableDefs}) { ${fields} }`, variables, aliases }
}
